// src/services/webBookingService.js
// 線上訂位設定 — Admin API 客戶端（Plan 090，frontend-booking 移植版）
// 對應後端 /api/web-booking/config（JWT + adminOnly）。
// 封面圖上傳沿用既有 presigned S3 流程（type='media'），就地內嵌、不依賴 faqService
// （frontend-booking 沒有 faqService）。用到的 images/* 端點是既有舊端點，prod 已有。
import apiService from './apiService';

const { apiRequest } = apiService;

// 封面圖公開網址走 CloudFront（永久、公開、無 JWT）；domain 同後端 cloudfront.config 預設。
// serve 端點（/api/images/public/serve/:token）需 JWT 且 24h 過期，不適合長存的公開封面 URL。
const CLOUDFRONT_DOMAIN = process.env.REACT_APP_CLOUDFRONT_DOMAIN || 'd3uyjtuwlqw6e9.cloudfront.net';

// GET /api/web-booking/config → { success, data:{ enabled, slug, coverImageUrl, ... } }
const getConfig = async () => {
  const res = await apiRequest('get', 'web-booking/config');
  return res.data;
};

// PUT /api/web-booking/config → { success, message, data }
const updateConfig = async (data) => {
  const res = await apiRequest('put', 'web-booking/config', data);
  return res.data;
};

// GET /api/web-booking/config/check-slug?slug= → { success, available, slug, reason? }
const checkSlug = async (slug) => {
  const res = await apiRequest('get', `web-booking/config/check-slug?slug=${encodeURIComponent(slug)}`);
  return res.data;
};

// 封面圖上傳：① 取 presigned URL（type='media'）② raw PUT 到 S3 ③ confirm → 回 CloudFront 公開網址。
// mimeType 一次算好、presigned 與 PUT 共用（避免 fallback 不同造成 S3 簽章 Content-Type 不符）。
// 失敗時 throw；呼叫端（WebBookingSettingsPage）可降級為手動貼網址。
const uploadCover = async (file) => {
  const mimeType = file.type || 'image/jpeg';

  // ① 取得 presigned 上傳連結
  const presignedRes = await apiRequest('post', 'images/pre-signed-urls', {
    files: [{ filename: file.name, mimeType, size: file.size || 0 }],
    type: 'media'
  });
  const body = presignedRes?.data?.data || presignedRes?.data || {};
  const list = body.images || body.uploadUrls || body.urls || [];
  const item = list[0];
  if (!item || !item.presignedUrl || !item.token) throw new Error('無法取得上傳連結');

  // ② 直傳 S3（raw fetch PUT — 不可帶 JWT 或多餘 header；Content-Type 必須等於簽章時的 mimeType）
  const putRes = await fetch(item.presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': mimeType }
  });
  if (!putRes.ok) throw new Error(`S3 上傳失敗: ${putRes.status}`);

  // ③ 確認上傳 → 後端回最終 s3Key
  const confirmRes = await apiRequest('post', 'images/confirm-upload', { token: item.token, fileSize: file.size });
  const s3Key = confirmRes?.data?.data?.s3Key;
  if (!s3Key) throw new Error('確認上傳失敗（缺 s3Key）');

  // 回傳 CloudFront 永久公開網址（去前導斜線，對齊後端 cloudfrontConfig.getUrl）
  return `https://${CLOUDFRONT_DOMAIN}/${s3Key.replace(/^\/+/, '')}`;
};

// 從菜單品項照片庫取圖（訂位頁相簿「從菜單選圖」用）：列出有照片的品項 → [{ name, imageUrl }]。
// 沿用既有 GET /api/menu/categories（Plan 106；未設菜單則回空陣列）。失敗降級為空。
const getMenuPhotos = async () => {
  try {
    const res = await apiRequest('get', 'menu/categories');
    const body = res?.data || {};
    const cats = body.categories || body.data || [];
    const out = [];
    const seen = new Set();
    cats.forEach((c) => (c.items || []).forEach((it) => {
      const url = it.imageUrl;
      if (url && !seen.has(url)) { seen.add(url); out.push({ name: it.name || '', imageUrl: url }); }
    }));
    return out;
  } catch (e) {
    return [];
  }
};

const webBookingService = { getConfig, updateConfig, checkSlug, uploadCover, getMenuPhotos };
export default webBookingService;
